import { AlertTriangle, CalendarClock, FlaskConical, Headset, Monitor, Plus, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Kpi, Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { Paged } from '@/components/common/Pager'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtMoney } from '../shared/format'
import { useCollections, useCrmOverview, useRequests, useTrials, useWork } from './api'
import { CollectionsList, RequestList, WorkList } from './components/RecordLists'
import { TrialList } from './components/TrialList'
import { CustomerPicker } from './components/CrmBits'
import { useCrmDeletes } from './components/useCrmDeletes'
import { useScopedDialog } from './components/useScopedDialog'
import { CRM_FINANCE_PERM } from './permissions'
import { PRIORITIES, REQUEST_STATUSES, WORK_KINDS, WORK_STATUSES, type Priority, type RequestStatus, type TrialStatus, type WorkKind, type WorkStatus } from './types'

export function DeliveryPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const work = useWork()
  const deletes = useCrmDeletes()
  const scoped = useScopedDialog()
  const [kind, setKind] = useState<WorkKind | 'all'>('all')
  const [status, setStatus] = useState<WorkStatus | 'all'>('all')
  const [customerId, setCustomerId] = useState('')
  const canWrite = can(['demos.manage', 'installations.manage'])
  const rows = (work.data ?? []).filter((w) => (kind === 'all' || w.kind === kind) && (status === 'all' || w.status === status))
  const trials = useTrials().data ?? []
  const [trialFilter, setTrialFilter] = useState<'open' | TrialStatus | 'all'>('open')
  const trialRows = trials.filter((x) => trialFilter === 'all' || (trialFilter === 'open' ? x.status === 'active' || x.status === 'expired' : x.status === trialFilter))
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.deliveryPage.title')}
        description={t('crm.deliveryPage.subtitle')}
        actions={
          canWrite ? (
            <>
              <CustomerPicker value={customerId} onChange={setCustomerId} />
              <Button disabled={!customerId} onClick={() => scoped.open(customerId, { kind: 'work', work: null })}>
                <Plus />
                {t('crm.work.add')}
              </Button>
            </>
          ) : null
        }
      />
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi icon={FlaskConical} tone="info" label={t('crm.trialStatus.active')} value={trials.filter((x) => x.status === 'active').length} />
        <Kpi icon={CalendarClock} tone="warning" label={t('crm.trial.endingSoon')} value={trials.filter((x) => x.status === 'active' && x.daysLeft <= 3).length} footer={t('crm.trial.endingSoonHint')} />
        <Kpi icon={AlertTriangle} tone="danger" label={t('crm.trialStatus.expired')} value={trials.filter((x) => x.status === 'expired').length} footer={t('crm.trial.expiredHint')} />
      </div>
      <Section
        className="mb-4"
        title={t('crm.trial.title')}
        icon={FlaskConical}
        bodyClassName="p-0"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={trialFilter} onValueChange={(v) => setTrialFilter(v as typeof trialFilter)}>
              <TabsList size="sm">
                {(['open', 'converted', 'cancelled', 'all'] as const).map((f) => (
                  <TabsTrigger key={f} value={f}>
                    {t(`crm.trial.filter.${f}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {canWrite ? (
              <Button size="sm" variant="outline" disabled={!customerId} onClick={() => scoped.open(customerId, { kind: 'trial' })}>
                <Plus />
                {t('crm.trial.add')}
              </Button>
            ) : null}
          </div>
        }
      >
        <Paged rows={trialRows} resetKey={trialFilter}>
          {(page) => <TrialList rows={page} showCustomer canWrite={canWrite} onExtend={(x) => scoped.open(x.customerId, { kind: 'extendTrial', trial: x })} onConvert={(x) => scoped.open(x.customerId, { kind: 'convertTrial', trial: x })} />}
        </Paged>
      </Section>
      <Section
        title={t('crm.work.title')}
        icon={Monitor}
        bodyClassName="p-0"
        actions={
          <div className="flex flex-wrap gap-2">
            <Tabs value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
              <TabsList size="sm">
                <TabsTrigger value="all">{t('common.all')}</TabsTrigger>
                {WORK_KINDS.map((k) => (
                  <TabsTrigger key={k} value={k}>
                    {t(`crm.workKind.${k}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Select size="sm" className="w-36" value={status} onChange={(v) => setStatus(v as typeof status)} aria-label={t('crm.fields.status')} options={[{ value: 'all', label: t('common.all') }, ...WORK_STATUSES.map((s) => ({ value: s, label: t(`crm.workStatus.${s}`) }))]} />
          </div>
        }
      >
        <Paged rows={rows} resetKey={[kind, status]}>
          {(page) => <WorkList rows={page} showCustomer canWrite={canWrite} onEdit={(w) => scoped.open(w.customerId, { kind: 'work', work: w })} onDelete={(w) => void deletes.work(w)} />}
        </Paged>
      </Section>
      {scoped.host}
    </>
  )
}

export function CollectionsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const collections = useCollections()
  const overview = useCrmOverview('all')
  const scoped = useScopedDialog()
  const [status, setStatus] = useState<'all' | 'open' | 'overdue' | 'paid'>('open')
  const [customerId, setCustomerId] = useState('')
  const canPay = can(CRM_FINANCE_PERM)
  const all = collections.data ?? []
  const rows = all.filter((c) => status === 'all' || (status === 'open' ? c.status !== 'paid' : c.status === status))
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.collectionsPage.title')}
        description={t('crm.collectionsPage.subtitle')}
        actions={
          canPay ? (
            <>
              <CustomerPicker value={customerId} onChange={setCustomerId} />
              <Button variant="outline" disabled={!customerId} onClick={() => scoped.open(customerId, { kind: 'invoice' })}>
                {t('crm.invoice.add')}
              </Button>
              <Button disabled={!customerId} onClick={() => scoped.open(customerId, { kind: 'collection' })}>
                <Plus />
                {t('crm.collection.add')}
              </Button>
            </>
          ) : null
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Kpi icon={Wallet} tone="warning" label={t('crm.kpi.pending')} value={overview.data ? fmtMoney(overview.data.pendingCollection) : '—'} />
          <Kpi icon={AlertTriangle} tone="danger" label={t('crm.collectionsPage.overdue')} value={all.filter((c) => c.status === 'overdue').length} />
          <Kpi icon={CalendarClock} tone="info" label={t('crm.collectionsPage.open')} value={all.filter((c) => c.status !== 'paid').length} />
        </div>
        <Section
          title={t('crm.collection.title')}
          icon={Wallet}
          bodyClassName="p-0"
          actions={
            <Tabs value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <TabsList size="sm">
                {(['open', 'overdue', 'paid', 'all'] as const).map((s) => (
                  <TabsTrigger key={s} value={s}>
                    {t(`crm.collectionsPage.filter.${s}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          }
        >
          <Paged rows={rows} resetKey={status}>
            {(page) => <CollectionsList rows={page} showCustomer canPay={canPay} onReceipt={(c) => scoped.open(c.customerId, { kind: 'receipt', collectionId: c.id })} />}
          </Paged>
        </Section>
      </div>
      {scoped.host}
    </>
  )
}

export function SupportPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const requests = useRequests()
  const deletes = useCrmDeletes()
  const scoped = useScopedDialog()
  const [status, setStatus] = useState<RequestStatus | 'all' | 'active'>('active')
  const [priority, setPriority] = useState<Priority | 'all'>('all')
  const [customerId, setCustomerId] = useState('')
  const canWrite = can('tickets.write')
  const all = requests.data ?? []
  const rows = all.filter((r) => (status === 'all' || (status === 'active' ? r.status !== 'resolved' : r.status === status)) && (priority === 'all' || r.priority === priority))
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.supportPage.title')}
        description={t('crm.supportPage.subtitle')}
        actions={
          canWrite ? (
            <>
              <CustomerPicker value={customerId} onChange={setCustomerId} />
              <Button disabled={!customerId} onClick={() => scoped.open(customerId, { kind: 'request', request: null })}>
                <Plus />
                {t('crm.request.add')}
              </Button>
            </>
          ) : null
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Kpi icon={Headset} tone="danger" label={t('crm.requestStatus.open')} value={all.filter((r) => r.status === 'open').length} />
          <Kpi icon={CalendarClock} tone="warning" label={t('crm.requestStatus.in_progress')} value={all.filter((r) => r.status === 'in_progress').length} />
          <Kpi icon={AlertTriangle} tone="primary" label={t('crm.supportPage.highPriority')} value={all.filter((r) => r.priority === 'high' && r.status !== 'resolved').length} />
        </div>
        <Section
          title={t('crm.request.title')}
          icon={Headset}
          bodyClassName="p-0"
          actions={
            <div className="flex flex-wrap gap-2">
              <Select size="sm" className="w-40" value={status} onChange={(v) => setStatus(v as typeof status)} aria-label={t('crm.fields.status')} options={[{ value: 'active', label: t('crm.supportPage.active') }, { value: 'all', label: t('common.all') }, ...REQUEST_STATUSES.map((s) => ({ value: s, label: t(`crm.requestStatus.${s}`) }))]} />
              <Select size="sm" className="w-36" value={priority} onChange={(v) => setPriority(v as typeof priority)} aria-label={t('crm.request.priority')} options={[{ value: 'all', label: t('common.all') }, ...PRIORITIES.map((p) => ({ value: p, label: t(`crm.priority.${p}`) }))]} />
            </div>
          }
        >
          <Paged rows={rows} resetKey={[status, priority]}>
            {(page) => <RequestList rows={page} showCustomer canWrite={canWrite} onEdit={(r) => scoped.open(r.customerId, { kind: 'request', request: r })} onDelete={(r) => void deletes.request(r)} />}
          </Paged>
        </Section>
      </div>
      {scoped.host}
    </>
  )
}
